"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSubjectByAbbreviation } from "@/lib/data/subjects/subjects";
import { logActivity } from "@/lib/client-activity-log";
import { appGetItem, appSetItem } from "@/lib/auth-storage";
import { authedFetch, authedFetchNow, getCachedIdToken } from "@/lib/api-client";

type SerializableRecord = Record<string, unknown>;

interface UserPreferences {
  // Home page preferences
  homeElectiveSubjects: string[];
  homeSubjectScores: Record<string, { current: string; min: string; max: string }>;
  
  // Recommended page preferences
  recommendedCareer: string;
  recommendedNoPreferenceUniversities: boolean;
  recommendedNoPreferenceDisciplines: boolean;
  recommendedSelectedUniversities: string[];
  recommendedSelectedDisciplines: string[];
  recommendedSelectedInterests: string[];
  recommendedOtherHobbies: string;
  recommendedCurrentYear: string;
  recommendedMbtiType: string;
  recommendedOtherInfo: string;
  
  // Recommended page output (generated recommendations)
  recommendedShowRecommendations: boolean;
  recommendedAiRecommendation: string;
  recommendedRecommendations: SerializableRecord[]; // Store serializable recommendation data
  recommendedSelectedProgrammesForAdd: SerializableRecord[]; // Store selected programmes before adding to recommendations
  
  // Priorities page preferences
  prioritiesSelectedProgrammes: SerializableRecord[]; // Store JupasPriority array
  
  // Home page preferences
  homeSubjects: SerializableRecord[]; // Store SubjectWithRange array
  homeResults: SerializableRecord[]; // Store EnhancedProgrammeScore array
  homeResultsCount?: number;
  homeEligibleCount?: number;
  homeActiveTab: string;
  homeExpandedRow: string | null;
  homeResultsGlobalFilter: string;
  homeResultsSorting: Array<{ id: string; desc: boolean }>;
  homeResultsUniversityFilter: string;
  homeResultsQuartileFilter: string;
  // Whether the results table under the "Calculate Score" button is showing.
  // Persisted so navigating away (e.g. to /recommended) and back does not wipe
  // the results the user already calculated.
  homeShowResults: boolean;
  
  // Results view preferences
  isTableView?: boolean; // true = table view, false = card view, undefined = auto
}

const defaultPreferences: UserPreferences = {
  homeElectiveSubjects: [],
  homeSubjectScores: {},
  recommendedCareer: "",
  recommendedNoPreferenceUniversities: false,
  recommendedNoPreferenceDisciplines: false,
  recommendedSelectedUniversities: [],
  recommendedSelectedDisciplines: [],
  recommendedSelectedInterests: [],
  recommendedOtherHobbies: "",
  recommendedCurrentYear: "",
  recommendedMbtiType: "",
  recommendedOtherInfo: "",
  recommendedShowRecommendations: false,
  recommendedAiRecommendation: "",
  recommendedRecommendations: [],
  recommendedSelectedProgrammesForAdd: [],
  prioritiesSelectedProgrammes: [],
  homeSubjects: [],
  homeResults: [],
  homeResultsCount: 0,
  homeEligibleCount: 0,
  homeActiveTab: "all",
  homeExpandedRow: null,
  homeResultsGlobalFilter: "",
  homeResultsSorting: [{ id: "university", desc: false }],
  homeResultsUniversityFilter: "all",
  homeResultsQuartileFilter: "",
  homeShowResults: false,
};

interface UserPreferencesContextType {
  preferences: UserPreferences;
  updatePreferences: (updates: Partial<UserPreferences>) => void;
  clearPreferences: (localOnly?: boolean) => Promise<void>;
  isLoading: boolean;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

const STORAGE_KEY = "jupas_user_preferences";

/** Browsers reject a `keepalive` fetch whose body exceeds 64KB; stay under it. */
const KEEPALIVE_BODY_LIMIT_BYTES = 60 * 1024;

/**
 * Console tracing for the path a picked level takes to the database.
 *
 * On so the ordering can be watched in the browser console — the store has to happen before the
 * re-score, and the request has to leave before a reload could cut it off. Set to false to silence
 * it; nothing else depends on it.
 */
const LOG_PREFERENCE_SAVES = true;

/** When the user last did something worth saving, for the "+Nms from click" figures below. */
let interactionMarkAt: number | null = null;

/** Start the clock. Called by the click handler, before anything else happens. */
export function markPreferenceInteraction(): void {
  interactionMarkAt = typeof performance !== "undefined" ? performance.now() : null;
}

function sinceInteraction(): string {
  if (interactionMarkAt === null) return "";
  return `  [+${Math.round(performance.now() - interactionMarkAt)}ms from click]`;
}

/** Timestamped console line, tagged so the whole path can be filtered on "[prefs]". */
export function logPreferenceSave(message: string, detail?: unknown): void {
  if (!LOG_PREFERENCE_SAVES || typeof window === "undefined") return;

  const now = new Date();
  const stamp = `${now.toLocaleTimeString("en-GB", { hour12: false })}.${String(
    now.getMilliseconds()
  ).padStart(3, "0")}`;
  const line = `%c[prefs]%c ${stamp}  ${message}`;
  const tag = "color:#e11d48;font-weight:700";

  if (detail === undefined) {
    console.log(line, tag, "color:inherit");
  } else {
    console.log(line, tag, "color:inherit", detail);
  }
}

/** The grades actually picked, as "Subject=grade", for a log line that stays one line. */
function describeScores(scores: UserPreferences["homeSubjectScores"] | undefined): string {
  const picked = Object.entries(scores ?? {})
    .filter(([, triple]) => triple?.current && triple.current !== "N/A")
    .map(([subject, triple]) => `${subject}=${triple.current}`);
  return picked.length > 0 ? picked.join(", ") : "(none picked)";
}

/** Payload size as something readable at a glance. */
function describeSize(body: string): string {
  return `${(body.length / 1024).toFixed(1)}KB`;
}

// Starting state for the core subjects: nothing selected. "N/A" is the stored
// "not set" value, so every grade button starts unpressed and the user picks
// their own grades instead of clearing pre-filled ones.
export const DEFAULT_CORE_SUBJECT_SCORES = {
  "Chinese Language": { current: "N/A", min: "N/A", max: "N/A" },
  "English Language": { current: "N/A", min: "N/A", max: "N/A" },
  "Mathematics": { current: "N/A", min: "N/A", max: "N/A" },
  "Citizenship and Social Development": { current: "N/A", min: "N/A", max: "N/A" }
};

// Compact human-readable summary of a preference value for activity logs
function summarizeValue(key: string, value: unknown): string {
  if (key === "homeSubjectScores" && value && typeof value === "object") {
    const subjectNames = Object.keys(value as Record<string, unknown>);
    return subjectNames.length > 0
      ? `subjects: ${subjectNames.join(", ")}`
      : "no subjects";
  }
  if (Array.isArray(value)) return `array(${value.length})`;
  if (value === null || value === undefined) return "null";
  if (typeof value === "object") {
    return `object(${Object.keys(value as object).length} keys)`;
  }
  const str = String(value);
  return str.length > 60 ? `${str.slice(0, 60)}…` : str;
}

// Map legacy/abbreviated subject keys to current full English subject names
function migrateSubjectKey(key: string): string {
  const subject = getSubjectByAbbreviation(key);
  if (subject) return subject.subject;
  // Fallback map for very old short codes not in subjects.ts
  const legacyMap: Record<string, string> = {
    CHIN: "Chinese Language",
    ENG: "English Language",
    MATH: "Mathematics",
    CSD: "Citizenship and Social Development",
  };
  return legacyMap[key] ?? key;
}

function sanitizePreferences(raw: Partial<UserPreferences>): UserPreferences {
  const legacyHomeResults = Array.isArray(raw.homeResults) ? raw.homeResults : [];
  const homeEligibleCount =
    typeof raw.homeEligibleCount === "number"
      ? raw.homeEligibleCount
      : legacyHomeResults.filter((item) => Boolean(item?.isEligible)).length;
  const homeResultsCount =
    typeof raw.homeResultsCount === "number"
      ? raw.homeResultsCount
      : legacyHomeResults.length;

  // Migrate legacy abbreviation/camelCase keys → full English subject names
  const rawScores = raw.homeSubjectScores;
  const migratedScores: UserPreferences["homeSubjectScores"] = {};
  if (rawScores) {
    for (const [key, value] of Object.entries(rawScores)) {
      const fullName = migrateSubjectKey(key);
      // Last write wins; full-name keys overwrite any migrated duplicates
      migratedScores[fullName] = value as { current: string; min: string; max: string };
    }
  }

  const rawElectives = Array.isArray(raw.homeElectiveSubjects) ? raw.homeElectiveSubjects : [];
  const migratedElectives = [...new Set(rawElectives.map(migrateSubjectKey))];

  return {
    ...defaultPreferences,
    ...raw,
    homeSubjectScores: Object.keys(migratedScores).length > 0 ? migratedScores : (raw.homeSubjectScores ?? defaultPreferences.homeSubjectScores),
    homeElectiveSubjects: migratedElectives,
    homeResultsGlobalFilter:
      typeof raw.homeResultsGlobalFilter === "string"
        ? raw.homeResultsGlobalFilter
        : defaultPreferences.homeResultsGlobalFilter,
    homeResultsSorting: Array.isArray(raw.homeResultsSorting)
      ? raw.homeResultsSorting.filter(
          (sort): sort is { id: string; desc: boolean } =>
            Boolean(sort)
            && typeof sort.id === "string"
            && typeof sort.desc === "boolean"
        )
      : defaultPreferences.homeResultsSorting,
    homeResultsUniversityFilter:
      typeof raw.homeResultsUniversityFilter === "string"
        ? raw.homeResultsUniversityFilter
        : defaultPreferences.homeResultsUniversityFilter,
    homeResultsQuartileFilter:
      raw.homeResultsQuartileFilter === "LQ"
      || raw.homeResultsQuartileFilter === "MED"
      || raw.homeResultsQuartileFilter === "UQ"
        ? raw.homeResultsQuartileFilter
        : "",
    homeResults: [],
    homeEligibleCount,
    homeResultsCount,
  };
}

// Check if user has modified any data from defaults
export function hasUserData(preferences: UserPreferences, favouritesCount: number): boolean {
  // Check if any elective subjects are selected
  if (preferences.homeElectiveSubjects.length > 0) return true;
  
  // Check if core subject scores differ from defaults
  const coreScores = preferences.homeSubjectScores;
  for (const [subject, defaultScore] of Object.entries(DEFAULT_CORE_SUBJECT_SCORES)) {
    const userScore = coreScores[subject];
    if (!userScore) continue;
    if (userScore.current !== defaultScore.current || 
        userScore.min !== defaultScore.min || 
        userScore.max !== defaultScore.max) {
      return true;
    }
  }
  
  // Check if any favourites exist
  if (favouritesCount > 0) return true;
  
  // Check if any AI recommendations exist
  if (preferences.recommendedRecommendations?.length > 0) return true;
  
  // Check recommended page data
  if (preferences.recommendedCareer) return true;
  if (preferences.recommendedSelectedUniversities.length > 0) return true;
  if (preferences.recommendedSelectedDisciplines.length > 0) return true;
  if (preferences.recommendedSelectedInterests.length > 0) return true;
  if (preferences.recommendedOtherHobbies) return true;
  if (preferences.recommendedCurrentYear) return true;
  if (preferences.recommendedMbtiType) return true;
  if (preferences.recommendedOtherInfo) return true;
  
  // Check priorities data
  if (preferences.prioritiesSelectedProgrammes?.length > 0) return true;
  
  return false;
}

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const { user: authUser, status } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isLoadingRef = React.useRef(true);
  const updateQueueRef = React.useRef<Partial<UserPreferences>[]>([]);
  const pendingSaveRef = React.useRef<UserPreferences | null>(null);
  /**
   * The newest preferences, whether or not React has rendered them yet: what updatePreferences()
   * merges the next change onto, and what the unload flush sends. Written only by
   * applyPreferences().
   */
  const preferencesRef = React.useRef<UserPreferences>(defaultPreferences);
  /** Body of the last request the server accepted, so the unload flush can skip a no-op save. */
  const lastSavedBodyRef = React.useRef<string | null>(null);
  /**
   * The preferences updatePreferences() already stored on its way through, so the effect below
   * does not store them a second time. Everything that does not go through updatePreferences —
   * the initial load, clearPreferences — still relies on that effect.
   */
  const lastPersistedRef = React.useRef<UserPreferences | null>(null);
  const isSavingRef = React.useRef(false);
  const lastSyncedUserIdRef = React.useRef<string | null>(null);

  /**
   * Set preferences, keeping preferencesRef in step.
   *
   * Every path that changes preferences goes through here. The ref is not a convenience copy any
   * more: it is what updatePreferences() merges the next change onto, so a setState that left it
   * behind would have the following change silently undo this one. That is exactly what a
   * functional update cannot express — it only produces its value once React re-renders, and
   * updatePreferences needs the value now, to store it.
   */
  const applyPreferences = useCallback((next: UserPreferences) => {
    preferencesRef.current = next;
    setPreferences(next);
  }, []);

  const flushQueuedUpdates = useCallback(() => {
    if (updateQueueRef.current.length === 0) {
      return;
    }

    const mergedUpdates = updateQueueRef.current.reduce<Partial<UserPreferences>>(
      (accumulator, update) => ({ ...accumulator, ...update }),
      {}
    );

    updateQueueRef.current = [];
    applyPreferences({ ...preferencesRef.current, ...mergedUpdates });
  }, [applyPreferences]);

  const finishLoading = useCallback(() => {
    isLoadingRef.current = false;
    setIsLoading(false);
    flushQueuedUpdates();
  }, [flushQueuedUpdates]);

  // Save to database helper function
  const saveToDatabase = useCallback(async (prefs: UserPreferences) => {
    if (status !== "authenticated" || !authUser) {
      logPreferenceSave("not signed in — grades stay on this device, nothing sent to the database");
      return;
    }

    pendingSaveRef.current = prefs;

    if (isSavingRef.current) {
      logPreferenceSave("a request is already in flight — folded into it, one will follow");
      return;
    }

    isSavingRef.current = true;

    try {
      while (pendingSaveRef.current) {
        const nextPreferences = pendingSaveRef.current;
        pendingSaveRef.current = null;

        const body = JSON.stringify({ preferences: nextPreferences });
        // Saves are no longer debounced, so the same payload can arrive twice — an unload flush
        // racing the ordinary save, or two callers publishing in the same commit. Neither needs
        // a second round trip.
        if (body === lastSavedBodyRef.current) {
          logPreferenceSave("database already holds this exact payload — no request sent");
          continue;
        }

        logPreferenceSave(
          `POST /api/user-data  ${describeSize(body)}  →  ${describeScores(nextPreferences.homeSubjectScores)}${sinceInteraction()}`
        );
        const startedAt = performance.now();

        // authedFetchNow, not authedFetch: this call reaches fetch() synchronously, so the
        // request is on the wire before this function yields. Awaiting a token here instead put
        // the dispatch in a continuation, which on the home page meant behind the re-score.
        const response = await authedFetchNow("/api/user-data", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
        });

        const elapsed = Math.round(performance.now() - startedAt);

        if (!response.ok) {
          logPreferenceSave(`POST failed — ${response.status} after ${elapsed}ms, grades NOT saved`);
          console.error(`Failed to save preferences: ${response.status}`);
          break;
        }

        // The route reports what it spent where; without it every millisecond between dispatch
        // and response looks the same, and network, token verification and the write are three
        // very different problems.
        const serverTiming = response.headers.get("Server-Timing");
        logPreferenceSave(
          `saved to the database — ${response.status}, round trip ${elapsed}ms${sinceInteraction()}` +
            (serverTiming ? `\n         server: ${serverTiming}` : "")
        );
        lastSavedBodyRef.current = body;
      }
    } catch (error) {
      logPreferenceSave("POST threw — grades NOT saved", error);
      console.error("Failed to save preferences to database:", error);
    } finally {
      isSavingRef.current = false;
    }
  }, [authUser, status]);

  /**
   * Store a set of preferences: to browser storage, and to the database.
   *
   * Called straight from updatePreferences() so that storing a value is the first thing that
   * happens to it, ahead of whatever the caller does next. Reaching it through the auto-save
   * effect instead cost two React commits and their passive-effect callbacks first, which is
   * time spent behind whatever else those commits render.
   */
  const persistPreferences = useCallback((prefs: UserPreferences) => {
    // The database first. It is the only part of this that has to beat a reload, and the
    // stringify-and-write below is synchronous work the request would otherwise be dispatched
    // behind. Browser storage is read back on the next load either way.
    if (status === "authenticated" && authUser) {
      void saveToDatabase(prefs);
    } else {
      logPreferenceSave("not signed in — no database write");
    }

    const serialized = JSON.stringify(prefs);
    appSetItem(STORAGE_KEY, serialized);
    logPreferenceSave(`browser storage written  ${describeSize(serialized)}${sinceInteraction()}`);
  }, [authUser, saveToDatabase, status]);

  /**
   * persistPreferences through a ref, so updatePreferences never changes identity when the auth
   * state resolves — half the effects on the home page are keyed on it.
   */
  const persistPreferencesRef = React.useRef(persistPreferences);
  useEffect(() => {
    persistPreferencesRef.current = persistPreferences;
  }, [persistPreferences]);

  // Initial load from storage after mount (client-side only)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const stored = appGetItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const sanitized = sanitizePreferences(parsed);
        applyPreferences(sanitized);
        appSetItem(STORAGE_KEY, JSON.stringify(sanitized));
      } catch (error) {
        console.error("Failed to parse stored preferences:", error);
      }
    }
    setIsLoaded(true);
  }, [applyPreferences]);

  useEffect(() => {
    if (!isLoaded || status === "loading") {
      return;
    }

    let isCancelled = false;

    const loadFromDatabase = async () => {
      if (status === "authenticated" && authUser) {
        lastSyncedUserIdRef.current = authUser.id;
        try {
          const response = await authedFetch("/api/user-data");
          if (response.ok) {
            const data = await response.json();
            if (data.preferences) {
              const sanitized = sanitizePreferences(data.preferences);
              if (!isCancelled) {
                applyPreferences(sanitized);
                // What the server already holds, so the unload flush does not write it straight
                // back when the user opens the page and leaves without changing anything.
                lastSavedBodyRef.current = JSON.stringify({ preferences: sanitized });
                appSetItem(STORAGE_KEY, JSON.stringify(sanitized));
                logActivity(
                  "preferences-loaded",
                  "User preferences loaded from database",
                  {
                    electiveCount: sanitized.homeElectiveSubjects.length,
                    scoreSubjectCount: Object.keys(sanitized.homeSubjectScores).length,
                  }
                );
              }
            } else {
              const stored = appGetItem(STORAGE_KEY);
              if (stored) {
                const parsed = JSON.parse(stored);
                await saveToDatabase(sanitizePreferences(parsed));
                logActivity(
                  "preferences-uploaded",
                  "Local preferences uploaded to database (no saved data found)"
                );
              }
            }
          } else if (response.status !== 401) {
            console.error(`Failed to load preferences from database: ${response.status}`);
          }
        } catch (error) {
          console.error("Failed to load preferences from database:", error);
        }
      } else {
        lastSyncedUserIdRef.current = null;
        const clearedPreferences = {
          ...defaultPreferences,
          homeSubjectScores: DEFAULT_CORE_SUBJECT_SCORES,
        };
        if (!isCancelled) {
          applyPreferences(clearedPreferences);
          appSetItem(STORAGE_KEY, JSON.stringify(clearedPreferences));
        }
      }
    };

    isLoadingRef.current = true;
    setIsLoading(true);

    void (async () => {
      await loadFromDatabase();
      if (!isCancelled) {
        finishLoading();
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [applyPreferences, finishLoading, isLoaded, saveToDatabase, authUser, status]);

  /**
   * Store anything that reached state without being stored on the way — in practice the updates
   * that were queued while preferences were loading and replayed by flushQueuedUpdates().
   *
   * It is no longer where a value first gets saved: updatePreferences() does that itself, before
   * it hands the value to React. preferencesRef is not maintained here either — applyPreferences()
   * is its only writer, and assigning it from a commit would be a step backwards, since a commit's
   * passive effect can run after a later updatePreferences has already moved the ref on.
   */
  useEffect(() => {
    // Don't save during initial loading
    if (!isLoaded || isLoadingRef.current) {
      return;
    }

    // Same race from the other side: this callback is running for a value that has already been
    // replaced, and writing it back would undo the replacement.
    if (preferences !== preferencesRef.current) {
      return;
    }

    // Already stored by updatePreferences, before the caller went on to do anything else with it.
    if (lastPersistedRef.current === preferences) {
      return;
    }

    persistPreferences(preferences);
  }, [preferences, isLoaded, persistPreferences]);

  /**
   * Write whatever is still in flight before the page goes away.
   *
   * The database write above is immediate now, so this covers the remaining gap: a reload that
   * lands inside the round trip of the save it triggered. `pagehide` and a `visibilitychange` to
   * hidden are the two events that fire reliably in that situation — `beforeunload` does not, on
   * mobile especially — and `keepalive` lets the request outlive the document that started it.
   *
   * The request is assembled and sent synchronously, without authedFetch: that awaits the Firebase
   * SDK for a token, and the continuation after an await in an unload handler frequently never
   * runs, so fetch() was never reached and this flush silently saved nothing — which is why a
   * grade picked and immediately followed by a reload was lost even though this handler ran.
   */
  useEffect(() => {
    if (!isLoaded || status !== "authenticated" || !authUser) {
      return;
    }

    const flush = () => {
      if (isLoadingRef.current) return;

      const body = JSON.stringify({ preferences: preferencesRef.current });
      if (body === lastSavedBodyRef.current) {
        logPreferenceSave("page going away — everything already saved, nothing to flush");
        return;
      }

      logPreferenceSave(
        `page going away — flushing ${describeSize(body)}  →  ${describeScores(preferencesRef.current.homeSubjectScores)}`
      );
      // Recorded before the request so a pagehide followed by a visibilitychange — the browser
      // fires both — does not send the same payload twice.
      lastSavedBodyRef.current = body;

      const headers: Record<string, string> = { "Content-Type": "application/json" };
      const token = getCachedIdToken();
      if (token) headers.Authorization = `Bearer ${token}`;

      void fetch("/api/user-data", {
        method: "POST",
        headers,
        body,
        // keepalive caps the body at 64KB and rejects outright above it, taking the whole flush
        // with it. A payload that large (a long list of AI recommendations) goes as an ordinary
        // request instead: it may not survive the unload, but it is not guaranteed to fail.
        keepalive: body.length <= KEEPALIVE_BODY_LIMIT_BYTES,
      }).then(
        (response) => {
          if (!response.ok) lastSavedBodyRef.current = null;
        },
        // The page is going away; all this can do is let a surviving tab retry.
        () => {
          lastSavedBodyRef.current = null;
        }
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };

    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isLoaded, authUser, status]);

  const updatePreferences = useCallback((updates: Partial<UserPreferences>) => {
    // If still loading, queue the update instead of applying it
    if (isLoadingRef.current) {
      updateQueueRef.current.push(updates);
      return;
    }
    
    // A caller re-publishing a value it already published — the home page does this, from a click
    // handler and again from an effect watching the same state — is not a change, and must not
    // reach the activity log, storage or the server.
    const hasChanges = Object.entries(updates).some(
      ([key, value]) => preferencesRef.current[key as keyof UserPreferences] !== value
    );
    if (!hasChanges) {
      logPreferenceSave(`no change in ${Object.keys(updates).join(", ")} — already stored`);
      return;
    }

    logPreferenceSave(`updatePreferences(${Object.keys(updates).join(", ")}) — storing now`);

    const next = { ...preferencesRef.current, ...updates };

    /*
     * Stored before setPreferences, not after it.
     *
     * The database write used to hang off an effect keyed on `preferences`, so a value only
     * started being saved two commits after the call that changed it — and on the home page the
     * very next thing to run is a re-score of every programme, a second of blocked main thread
     * that the save then sat behind. Doing it here means the request is already on its way by the
     * time the caller returns, whatever the caller does next.
     *
     * This is also why `next` is built from preferencesRef rather than from a functional update:
     * the value has to exist now, not when React gets round to re-rendering. The ref is kept
     * authoritative by every path that sets preferences.
     */
    lastPersistedRef.current = next;
    persistPreferencesRef.current(next);

    applyPreferences(next);

    /*
     * The activity log last, deliberately.
     *
     * logActivity reads every stored entry back, parses them, unshifts, re-serialises up to 300
     * of them and writes the lot to the in-memory app store, then dispatches a DOM event that re-renders
     * whatever is listening — all synchronously. In front of the save that was several
     * milliseconds of the request's head start spent on bookkeeping.
     */
    const changedKeys = Object.keys(updates);
    if (changedKeys.length > 0) {
      const parts = changedKeys.map((key) => {
        const value = updates[key as keyof UserPreferences];
        return `${key}: ${summarizeValue(key, value)}`;
      });
      logActivity(
        "preference-update",
        `Preferences updated: ${parts.join("; ")}`,
        { keys: changedKeys }
      );
    }
  }, [applyPreferences]);

  const clearPreferences = useCallback(async (localOnly = false) => {
    console.log("UserPreferencesContext: Clearing all preferences");
    logActivity(
      "preferences-cleared",
      localOnly
        ? "All preferences cleared (this device only)"
        : "All preferences cleared",
      { localOnly }
    );
    
    const clearedPreferences = {
      ...defaultPreferences,
      homeSubjectScores: DEFAULT_CORE_SUBJECT_SCORES
    };
    
    applyPreferences(clearedPreferences);
    appSetItem(STORAGE_KEY, JSON.stringify(clearedPreferences));

    if (!localOnly && status === "authenticated" && authUser && !isSavingRef.current) {
      isSavingRef.current = true;
      try {
        await authedFetch("/api/user-data", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ preferences: clearedPreferences }),
          keepalive: true,
        });
      } catch (error) {
        console.error("Failed to save cleared preferences to database:", error);
      } finally {
        isSavingRef.current = false;
      }
    }
  }, [applyPreferences, status, authUser]);

  return (
    <UserPreferencesContext.Provider value={{ preferences, updatePreferences, clearPreferences, isLoading }}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  // debugger;
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error("useUserPreferences must be used within a UserPreferencesProvider");
  }
  return context;
}
